import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { FaPlus, FaEdit, FaTrash, FaLayerGroup } from 'react-icons/fa';
import ConfirmationModal from '../DeleteConfirmationModal';
import './KeyMessagesList.css';
import KeyMessageForm from './KeyMessageForm';

const KeyMessagesList = () => {
  const [themes, setThemes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isAddThemeModalOpen, setIsAddThemeModalOpen] = useState(false);
  const [newTheme, setNewTheme] = useState({ name: '', description: '' });

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: 'Cancel',
    type: 'danger',
    onConfirm: null,
    isLoading: false
  });

  // Helper functions for confirmation modal
  const openConfirmationModal = ({ title, message, confirmText, type = 'danger', onConfirm }) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      confirmText,
      cancelText: 'Cancel',
      type,
      onConfirm,
      isLoading: false
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
  };

  const setConfirmationLoading = (isLoading) => {
    setConfirmationModal(prev => ({ ...prev, isLoading }));
  };

  useEffect(() => {
    fetchThemes();
    fetchMessages();
  }, []);

  const fetchThemes = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/key-message-themes');
      setThemes(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching themes:', err);
      setError('Failed to load themes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/key-messages');
      setMessages(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleThemeSelect = (theme) => {
    setSelectedTheme(theme);
  };

  const handleAllThemes = () => {
    setSelectedTheme(null);
  };

  const handleAddMessage = () => {
    setSelectedMessage(null);
    setIsAddModalOpen(true);
  };

  const handleEditMessage = (message) => {
    setSelectedMessage(message);
    setIsEditModalOpen(true);
  };

  const handleDeleteMessage = (messageId) => {
    const message = messages.find(m => m.id === messageId);
    const messageTitle = message?.title || 'this message';
    
    openConfirmationModal({
      title: 'Delete Key Message',
      message: `Are you sure you want to delete "${messageTitle}"? This action cannot be undone.`,
      confirmText: 'Delete Message',
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await apiClient.delete(`/key-messages/${messageId}`);
          fetchMessages();
          closeConfirmationModal();
        } catch (err) {
          console.error('Error deleting message:', err);
          setError('Failed to delete message. Please try again.');
          closeConfirmationModal();
        }
      }
    });
  };

  const handleModalClose = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedMessage(null);
  };

  const handleModalSubmit = async (formData) => {
    try {
      if (selectedMessage) {
        await apiClient.put(`/key-messages/${selectedMessage.id}`, formData);
      } else {
        await apiClient.post('/key-messages', formData);
      }
      fetchMessages();
      handleModalClose();
    } catch (err) {
      console.error('Error saving message:', err);
      setError('Failed to save message. Please try again.');
    }
  };

  const handleAddTheme = () => {
    setNewTheme({ name: '', description: '' });
    setIsAddThemeModalOpen(true);
  };

  const handleThemeModalClose = () => {
    setIsAddThemeModalOpen(false);
    setNewTheme({ name: '', description: '' });
  };

  const handleThemeInputChange = (e) => {
    const { name, value } = e.target;
    setNewTheme(prev => ({ ...prev, [name]: value }));
  };

  const handleThemeModalSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/key-message-themes', newTheme);
      fetchThemes();
      handleThemeModalClose();
    } catch (err) {
      setError('Failed to create theme. Please try again.');
    }
  };

  const handleDeleteTheme = (themeId, themeName) => {
    openConfirmationModal({
      title: 'Delete Theme',
      message: `Are you sure you want to delete the theme "${themeName}"? All associated messages will be set to Unknown. This action cannot be undone.`,
      confirmText: 'Delete Theme',
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await apiClient.delete(`/key-message-themes/${themeId}`);
          fetchThemes();
          fetchMessages();
          closeConfirmationModal();
        } catch (err) {
          setError('Failed to delete theme. Please try again.');
          closeConfirmationModal();
        }
      }
    });
  };

  if (loading) {
    return <div className="loading">Loading key messages...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="key-messages-container">
      <div className="themes-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Message Themes</h3>
          <button className="add-theme-button" onClick={handleAddTheme} title="Add Theme">
            <FaPlus />
          </button>
        </div>
        <div className="themes-list">
          <div
            className={`theme-item all-themes ${selectedTheme === null ? 'selected' : ''}`}
            onClick={handleAllThemes}
            title="Show all messages"
          >
            <FaLayerGroup style={{ marginRight: 6 }} /> All Themes
          </div>
          {themes.map(theme => (
            <div
              key={theme.id}
              className={`theme-item ${selectedTheme?.id === theme.id ? 'selected' : ''}`}
              onClick={() => handleThemeSelect(theme)}
              title={theme.description}
            >
              {theme.name}
              <button
                className="delete-theme-btn"
                title="Delete Theme"
                onClick={e => { e.stopPropagation(); handleDeleteTheme(theme.id, theme.name); }}
                style={{ marginLeft: 8, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <FaTrash />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="messages-section">
        <div className="messages-header">
          <h3 style={{ fontWeight: 600, color: '#1a365d' }}>
            {selectedTheme ? `${selectedTheme.name} Messages` : 'All Messages'}
          </h3>
          <button className="add-button" onClick={handleAddMessage}>
            <FaPlus /> Add Message
          </button>
        </div>

        <div className="messages-list">
          {messages
            .filter(msg => !selectedTheme || msg.theme_id === selectedTheme.id)
            .map(message => (
              <div key={message.id} className="message-item message-card">
                <div className="message-content">
                  <h4 style={{ color: '#2563eb', marginBottom: 6 }}>{message.title}</h4>
                  <div className="message-meta">
                    <span className="meta-chip">{themes.find(t => t.id === message.theme_id)?.name || 'Unknown Theme'}</span>
                    <span className="meta-chip">{message.tone_purpose}</span>
                    <span className="meta-chip">{message.stage_tag}</span>
                    <span className={`meta-chip status-${message.status.toLowerCase()}`}>{message.status}</span>
                  </div>
                  <p style={{ marginTop: 10 }}>{message.supporting_statement}</p>
                </div>
                <div className="message-actions">
                  <button onClick={() => handleEditMessage(message)} title="Edit">
                    <FaEdit />
                  </button>
                  <button onClick={() => handleDeleteMessage(message.id)} title="Delete">
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          {messages.filter(msg => !selectedTheme || msg.theme_id === selectedTheme.id).length === 0 && (
            <div className="no-messages">No messages found for this theme.</div>
          )}
        </div>
      </div>

      {(isAddModalOpen || isEditModalOpen) && (
        <KeyMessageForm
          isOpen={isAddModalOpen || isEditModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          themes={themes}
          initialData={selectedMessage}
        />
      )}

      {isAddThemeModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Theme</h3>
              <button onClick={handleThemeModalClose} className="close-button">&times;</button>
            </div>
            <form onSubmit={handleThemeModalSubmit}>
              <div className="form-group">
                <label htmlFor="theme-name">Theme Name</label>
                <input
                  id="theme-name"
                  name="name"
                  value={newTheme.name}
                  onChange={handleThemeInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="theme-description">Description</label>
                <textarea
                  id="theme-description"
                  name="description"
                  value={newTheme.description}
                  onChange={handleThemeInputChange}
                  required
                  rows={3}
                />
              </div>
              <div className="modal-footer">
                <button type="button" onClick={handleThemeModalClose} className="button button-secondary">
                  Cancel
                </button>
                <button type="submit" className="button button-primary">
                  Create Theme
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        type={confirmationModal.type}
        onConfirm={confirmationModal.onConfirm}
        onClose={closeConfirmationModal}
        isLoading={confirmationModal.isLoading}
      />
    </div>
  );
};

export default KeyMessagesList; 